import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import DetectorController, {
  TouchRateDataType,
  TouchLogDataType,
} from '../../controller/DetectorController';
import 'moment/locale/ko';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import DateTab from '../../components/DateTab';
import HotelTabletTouchLogModal from '../../components/modal/hotelTabletTouchLogModal';
import Loading from '../../components/Loading';
import ExcelJS from 'exceljs';

interface ColumnConfig {
  header: string;
  key: keyof Omit<TouchRateDataType, 'device'>; // 'device' 속성은 렌더링에서 제외
  width: number;
  transform?: (value: any, row: TouchRateDataType) => React.ReactNode;
}

const formatSecondsToHMS = (totalSeconds: number): string => {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '00:00:00';
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

const columnsConfig: ColumnConfig[] = [
  { header: '호텔명', key: 'store_name', width: 25 },
  { header: '전체 기기 수', key: 'real_total_store_device', width: 15 },
  { header: '이용 객실 수', key: 'total_store_touch', width: 15 },
  { header: '이용률', key: 'today_touch_rate', width: 12, transform: (val) => `${val}%` },
  { header: '전체 터치 수', key: 'total_device_touch_sum', width: 15 },
  { header: '전체 이용 시간', key: 'total_stay_seconds', width: 20, transform: (val) => formatSecondsToHMS(val) },
  { header: '평균 터치 수', key: 'adver_store_touch', width: 15 },
  { header: '평균 이용 시간', key: 'adver_stay_seconds', width: 20, transform: (val) => formatSecondsToHMS(val) },
];


const HotelDeviceTouch = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [pageCount, setPageCount] = useState<number>(1);
  const [openDetailIndex, setOpenDetailIndex] = useState<number | null>(null);
  const [storeTouchRateData, setStoreTouchRateData] = useState<TouchRateDataType[]>([]);
  const [detailLogData, setDetailLogData] = useState<TouchLogDataType[]>([]);
  const [id, setId] = useState<number>();
  const [type, setType] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setHours(0, 0, 0, 0);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  const [startDate, setStartDate] = useState<Date | null>(getYesterday());

  const openPathLog = async (newStartDate: Date | null) => {
    if (!newStartDate) return;
    const logDate = moment(newStartDate).format('YYYY-MM-DD');
    await getStoreTouchRate(logDate);
    setOpenDetailIndex(null);
  };

  const toggleDetail = (index: number) => {
    setOpenDetailIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  const openPathDetailLog = (type: string, id?: number) => {
    setId(id);
    setType(type);
    setIsModalOpen(true);
  };

  const dateTabContentData = [
    {
      id: 2,
      tab: '날짜 선택',
    },
  ];

  const totalValues = React.useMemo(() => {
    return storeTouchRateData.reduce(
      (acc, data) => {
        acc.totalStoreDevicesSum += data.real_total_store_device;
        acc.totalStoreTouchSum += data.total_store_touch;
        acc.totalDeviceTouchSum += data.total_device_touch_sum;
        acc.totalStaySecondsSum += data.total_stay_seconds;
        return acc;
      },
      {
        totalStoreDevicesSum: 0,
        totalStoreTouchSum: 0,
        totalDeviceTouchSum: 0,
        totalStaySecondsSum: 0,
      }
    );
  }, [storeTouchRateData]);

  const handleExcelDownload = async () => {
    if (!storeTouchRateData || storeTouchRateData.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }
    setIsDownloading(true);

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('기기 이용 현황');
      worksheet.columns = columnsConfig.map(({ header, key, width }) => ({ header, key, width }));

      const dataToExport = storeTouchRateData.map(item => {
        const rowData: { [key: string]: any } = {};
        columnsConfig.forEach(col => {
          const value = item[col.key];
          rowData[col.key] = col.transform ? col.transform(value, item) : value;
        });
        return rowData;
      });
      worksheet.addRows(dataToExport);

      worksheet.getRow(1).eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } };
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      const { totalStoreDevicesSum, totalStoreTouchSum, totalDeviceTouchSum, totalStaySecondsSum } = totalValues;
      const totalsRowData = {
        store_name: '합계',
        real_total_store_device: totalStoreDevicesSum,
        total_store_touch: totalStoreTouchSum,
        today_touch_rate: `${(totalStoreDevicesSum > 0 ? (totalStoreTouchSum / totalStoreDevicesSum * 100) : 0).toFixed(1)}%`,
        total_device_touch_sum: totalDeviceTouchSum,
        total_stay_seconds: formatSecondsToHMS(totalStaySecondsSum),
        adver_store_touch: (totalStoreTouchSum > 0 ? (totalDeviceTouchSum / totalStoreTouchSum) : 0).toFixed(1),
        adver_stay_seconds: formatSecondsToHMS(totalStoreTouchSum > 0 ? (totalStaySecondsSum / totalStoreTouchSum) : 0),
      };
      const totalRow = worksheet.addRow(totalsRowData);

      totalRow.font = { bold: true };
      worksheet.mergeCells(`A${totalRow.number}`);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `기기_이용_현황_${moment(startDate).format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export failed:", error);
      alert("엑셀 파일을 생성하는 중 오류가 발생했습니다.");
    } finally {
      setIsDownloading(false);
    }
  };


  const getStoreTouchRate = async (logDate: string) => {
    setIsLoading(true);
    try {
      const oData = { search_date: logDate };
      const response = await DetectorController.getStoreTouchRate(oData);
      if (response && response.data) {
        setStoreTouchRateData(response.data.data.touch_rate_data);
      }
    } catch (err) {
      console.log('err-->', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStoreTouchLog = async (page: number) => {
    setIsModalLoading(true);
    try {
      const limit = 10;
      const offset = (page - 1) * limit;
      const oData: any = {
        search_date: moment(startDate).format('YYYY-MM-DD'),
        limit: limit,
        offset: offset,
      };
      if (type === 'device') {
        oData.user_token_id = id;
      } else if (type === 'hotel') {
        oData.store_id = id;
      }
      const response = await DetectorController.getStoreTouchLog(oData);

      if (response && response.data) {
        setDetailLogData(response.data.data.touch_log_data);
        setPageCount(Math.ceil(response.data.data.total_count / 10));
      }
    } catch (err) {
      console.log('err-->', err);
    } finally {
      setIsModalLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) { getStoreTouchLog(1); }
  }, [isModalOpen]);

  useEffect(() => {
    if (startDate) {
      const logDate = moment(startDate).format('YYYY-MM-DD');
      getStoreTouchRate(logDate);
    }
  }, [startDate]);

  return (
    <Style>
      <div className="wrap">
        <button className="backButton" onClick={() => navigate('/dashboard')}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_home.svg`} alt="뒤로가기버튼" />
        </button>
        <h1>기기 이용 현황</h1>
        <DateTab type="oneDay" openPathLog={openPathLog} changeStartDate={setStartDate} dateTabContentData={dateTabContentData} maxDate={getYesterday()} />
        {isLoading ? (
          <div className="loadingWrap"><Loading /></div>
        ) : (
          <>
            {storeTouchRateData.length > 0 ? (
              <div className="tableWrap">
                <div className="totalLogButtonWrap">
                  <button className="excelDownloadButton" onClick={handleExcelDownload} disabled={isDownloading}>
                    {isDownloading ? '생성 중...' : '엑셀 다운로드'}
                  </button>
                  <button className="totalLogButton" onClick={() => openPathDetailLog('')}>
                    전체 이용 현황 로그
                  </button>
                </div>
                <div className="totalTable header">
                  <h3 className="detailButtonText">자세히보기</h3>
                  {columnsConfig.map(col => <h3 key={col.key}>{col.header}</h3>)}
                  <h3 className="detailButtonText">호텔 이용 현황 로그</h3>
                </div>
                {storeTouchRateData.map((data, index: number) => (
                  <React.Fragment key={data.store_id || index}>
                    <div className={`totalTable body ${openDetailIndex === index && 'active'}`} onClick={() => toggleDetail(index)}>
                      <button className="detailButton image">
                        <img src={`${process.env.PUBLIC_URL}/svg/ic_arrow_${openDetailIndex === index ? 'up' : 'down'}.svg`} alt="화살표" />
                      </button>
                      {columnsConfig.map(col => {
                        const value = data[col.key];
                        const displayValue = col.transform ? col.transform(value, data) : value;
                        return <h3 key={col.key}>{displayValue}</h3>
                      })}
                      <button onClick={(e) => { e.stopPropagation(); openPathDetailLog('hotel', data.store_id); }} className="detailButton">
                        <h5>호텔 통합 로그</h5>
                      </button>
                    </div>
                    {openDetailIndex === index && (
                      <div className="detailBoxWrap">
                        {data.device.map((deviceData, idx) => (
                          <div key={deviceData.user_token_id || idx} className="detailBox">
                            <div>
                              <h3>{deviceData.name}</h3>
                              <h4 className="totalTouch">터치 총합: {deviceData.total_device_touch}</h4>
                              <h4>마지막 터치: {deviceData.latest_touch}</h4>
                              <h4>마지막 터치 시간: {deviceData.latest_touch_at}</h4>
                              <h4>기기 이용 시간: {formatSecondsToHMS(deviceData.stay_seconds)}</h4>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); openPathDetailLog('device', deviceData.user_token_id); }}>
                              상세보기
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
                <div className="totalTable totalLine">
                  <h3 className="detailButtonText"></h3>
                  <h3>합계</h3>
                  <h3>{totalValues.totalStoreDevicesSum}</h3>
                  <h3>{totalValues.totalStoreTouchSum}</h3>
                  <h3>{(totalValues.totalStoreDevicesSum > 0 ? (totalValues.totalStoreTouchSum / totalValues.totalStoreDevicesSum * 100) : 0).toFixed(1)}%</h3>
                  <h3>{totalValues.totalDeviceTouchSum}</h3>
                  <h3>{formatSecondsToHMS(totalValues.totalStaySecondsSum)}</h3>
                  <h3>{(totalValues.totalStoreTouchSum > 0 ? (totalValues.totalDeviceTouchSum / totalValues.totalStoreTouchSum) : 0).toFixed(1)}</h3>
                  <h3>{formatSecondsToHMS(totalValues.totalStoreTouchSum > 0 ? (totalValues.totalStaySecondsSum / totalValues.totalStoreTouchSum) : 0)}</h3>
                  <h3></h3>
                </div>
              </div>
            ) : (
              <p>조회된 터치 내역이 없습니다.</p>
            )}
          </>
        )}
      </div>
      <HotelTabletTouchLogModal isVisible={isModalOpen} setVisible={setIsModalOpen} data={detailLogData} pageCount={pageCount} setPageCount={setPageCount} getStoreTouchLog={getStoreTouchLog} isModalLoading={isModalLoading} />
    </Style>
  );
};

export default HotelDeviceTouch;

const Style = styled.div`
  h1 { font-size: 24px; margin-bottom: 16px; }
  h2 { font-size: 16px; font-weight: 500; }
  h3 { flex: 1; font-size: 16px; text-align: center; font-weight: 400; color: #333; }
  h4 { font-size: 14px; font-weight: 400; color: #333; line-height: 20px; }
  h5 { font-size: 14px; font-weight: 400; }
  .wrap { padding: 3vw; }
  .loadingWrap { height: 80vh; display: flex; align-items: center; justify-content: center; }
  .totalLogButtonWrap { display: flex; gap: 10px; }
  .excelDownloadButton {
    padding: 10px 40px; margin-left: auto; margin-bottom: 14px;
    background: #007B43; color: #fff; border: none; outline: none;
    border-radius: 4px; cursor: pointer;
    &:disabled { background: #999; cursor: not-allowed; }
  }
  .totalLogButton {
    padding: 10px 40px; margin-bottom: 14px; background: #40977f;
    color: #fff; border: none; outline: none; border-radius: 4px;
  }
  .tableWrap { margin-top: 20px; }
  .totalTable { width: 100%; padding: 12px 1vw; display: flex; align-items: center; justify-content: space-between; gap: 1%; }
  .totalTable.header {
    position: sticky; top: 0; background: #efefef;
    border-top-right-radius: 4px; border-top-left-radius: 4px;
    h3 { font-weight: 500;  flex: 1; }
  }
  .totalTable.active { background: #ecf5f3; h3 { font-weight: 700; } }
  .totalTable.body { border-top: 1px solid #cecece; }
  .detailButton.image, .detailButtonText { width: 70px !important; min-width: 70px !important; flex: 1; }
  .detailButton { flex: 1; color: #fff; border-radius: 4px; border: none; background: none; outline: none; }
  .detailButton h5 { display: inline; padding: 6px 20px; background: #4dd2ae; color: #fff; border-radius: 4px; }
  .detailBoxWrap { display: flex; flex-wrap: wrap; padding: 1vh 1vw; gap: 10px; background: #ecf5f3; }
  .detailBox {
    display: flex; flex-direction: column; justify-content: space-between;
    max-width: 200px; padding: 10px; background: #fff;
    border: 1px solid rgb(206, 206, 206); border-radius: 4px;
    h3 { font-weight: 600; padding-bottom: 4px; border-bottom: 1px solid rgb(206, 206, 206); }
    h4 { padding-top: 4px; }
    button {
      width: 100%; margin-top: 4px; padding: 6px 0 4px 0; background: #ecf5f3;
      border: 1px solid #40977f; color: #40977f; font-size: 12px;
      font-weight: 700; outline: none; border-radius: 4px;
    }
  }
  .totalTouch { margin-top: 4px; font-size: 16px; font-weight: 500; line-height: 24px; color: #000; }
  .totalLine { border-top: 2px solid gray; }
`;
